import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { TenantsService } from "../../../../services/tenants.service";
import { OrganizationsService } from "../../../../services/organizations.service";
import { CepRule } from "../../../../model/cep-rule";
import { Entity } from "../../../../model/entity";
import { EntityMeasurement } from "../../../../model/entity-measurement";
import { EntityMeasurementFilters } from "../../../../model/filters/entity-measurement-filters";
import { EntityFilters } from "../../../../model/filters/entity-filters";
import { EntitiesResponse } from "../../../../model/responses/entities-response";
import { SelectedEntity } from "../../../../model/selected-entity";
import { CommonMessagesService } from "../../../../services/common-messages.service";
import { SortOrder } from "../../../../model/sort-order";
import { TranslocoService } from "@ngneat/transloco";

@Component({
  selector: 'app-entities-measurement-selector',
  templateUrl: './entities-measurement-selector.component.html',
  styleUrls: ['./entities-measurement-selector.component.scss']
})
export class EntitiesMeasurementSelectorComponent {

  readonly DEFAULT_MEASUREMENT_SORT_FIELD = 'name';
  readonly DEFAULT_MEASUREMENT_SORT_ORDER = 1;

  readonly DEFAULT_ENTITY_SORT_FIELD = 'identifier';
  readonly DEFAULT_ENTITY_SORT_ORDER = 1;

  @Input() cepRule: CepRule;
  @Input() hasBeenOpened: boolean;

  @Output() hasBeenOpenedChange : EventEmitter<boolean> = new EventEmitter();
  @Output() visibleChange: EventEmitter<boolean>;
  @Output() selectedEntitiesChange: EventEmitter<SelectedEntity[]>;

  dialogVisible: boolean;

  measurements: EntityMeasurement[];
  totalMeasurements: number;
  measurementFilters: EntityMeasurementFilters;
  loadingMeasurements: boolean;
  measurementsFirst: number;
  measurementsRows: number;

  entities: Entity[];
  totalEntities: number;
  entityFilters: EntityFilters;
  loadingEntities: boolean;
  entitiesFirst: number;
  entitiesRows: number;

  selected: SelectedEntity[];
  selectedFirst: number;
  selectedRows: number;

  providedSelection: SelectedEntity[];
  selectedMeasurement: EntityMeasurement;

  entityTypes: SelectItem[];
  measurementUnits: SelectItem[];

  constructor(private tenantsService: TenantsService,
              private organizationsService: OrganizationsService,
              private commonMessagesService: CommonMessagesService,
              private i18n: TranslocoService) {
    this.visibleChange = new EventEmitter<boolean>();
    this.selectedEntitiesChange = new EventEmitter<SelectedEntity[]>();

    this.initializeValues();
  }

  @Input()
  set selectedEntities(selected: SelectedEntity[]) {
    this.providedSelection = selected;
    this.setSelectedToProvided();
  }

  @Input()
  set visible(visible: boolean) {
    this.setSelectedToProvided();
    this.dialogVisible = visible;
    if (visible && !this.hasBeenOpened) {
      this.loadEntityMeasurements(0, this.measurementsRows);
      this.loadEntityTypes();
    } else if(visible && this.providedSelection.length == 0) {
      this.selectedMeasurement = undefined;
      this.initializeValues();
    }
  }

  private initializeValues() {
    this.measurementsFirst = 0;
    this.measurementsRows = 5;
    this.measurementFilters = new EntityMeasurementFilters();
    this.measurementFilters.sortField = this.DEFAULT_MEASUREMENT_SORT_FIELD;
    this.measurementFilters.sortOrder = this.DEFAULT_MEASUREMENT_SORT_ORDER;
    this.entitiesFirst = 0;
    this.entitiesRows = 5;
    this.entityFilters = new EntityFilters();
    this.entityFilters.sortField = this.DEFAULT_ENTITY_SORT_FIELD;
    this.entityFilters.sortOrder = this.DEFAULT_ENTITY_SORT_ORDER;
    this.selectedFirst = 0;
    this.selectedRows = 5;
  }

  private setSelectedToProvided(): void {
    this.selected = [...this.providedSelection];
  }

  private loadEntityMeasurements(offset: number, limit: number): void {
    this.loadingMeasurements = true;
    this.measurements = new Array(this.measurementsRows);
    this.trimFieldsForm(this.measurementFilters);
    this.tenantsService.getTenantEntityMeasurements(this.cepRule.tenant.uuid, offset, limit, this.measurementFilters)
      .subscribe((response) => {
        this.measurements = response.entityMeasurements;
        this.measurements.map((m: EntityMeasurement) => {
          m.id = m.name.concat("_", m.measurementTypeUnit);
        });
        this.totalMeasurements = response.totalElements;
        if(this.measurementUnits == undefined) {
          this.loadMeasurementUnits();
        }
        if (this.selected && this.selected.length > 0) {
          this.selectedMeasurement = {
            id: this.selected[0].measurementName.concat("_", this.selected[0].measurementTypeUnit),
            name: this.selected[0].measurementName,
            measurementTypeUnit: this.selected[0].measurementTypeUnit
          };
          this.onMeasurementSelect2(this.selectedMeasurement);
        }
        if(!this.hasBeenOpened)
          this.hasBeenOpenedChange.emit(true);
        this.loadingMeasurements = false;
      });
  }

  private loadEntities(offset: number, limit: number): void {
    this.loadingEntities = true;
    this.entities = new Array(this.entitiesRows);
    this.trimFieldsForm(this.entityFilters);
    if (this.cepRule.organization == undefined) {
      this.tenantsService.getTenantEntitiesFromMeasurement(this.cepRule.tenant.uuid, offset, limit, this.entityFilters)
        .subscribe((response) => {
          this.responseLoadEntities(response);
        });
    } else {
      this.organizationsService.getOrganizationEntitiesFromMeasurement(this.cepRule.organization.uuid, offset, limit, this.entityFilters)
        .subscribe((response) => {
          this.responseLoadEntities(response);
        });
    }
  }

  responseLoadEntities(response: EntitiesResponse) {
    this.entities = response.entities;
    this.totalEntities = response.totalElements;
    this.loadingEntities = false;
  }

  private loadEntityTypes(): void {
    this.entityTypes = [
      {label: this.i18n.translate('model.device'), value: 'DEVICE'},
      {label: this.i18n.translate('model.asset'), value: 'ASSET'}
    ];
  }

  private loadMeasurementUnits(): void {
    this.measurementUnits = this.measurements.map((m: EntityMeasurement) => m.measurementTypeUnit)
      .filter(measurementTypeUnit => measurementTypeUnit !== undefined)
      .filter((value, index, self) => self.indexOf(value) === index)
      .map(value => ({value})).sort();
  }

  public applyEntityFilters(): void {
    this.entitiesFirst = 0;
    this.loadEntities(0, this.entitiesRows);
  }

  public applyMeasurementFilters(): void {
    this.measurementsFirst = 0;
    this.loadEntityMeasurements(0, this.measurementsRows);
  }

  public onMeasurementSelect(measurement: EntityMeasurement): void {
    if (measurement) {
      if (this.selected.length > 0) {
        this.commonMessagesService.showDeleteEntitiesSelectedConfirmation(() => {
          this.selected = [];
          this.onMeasurementSelect2(measurement);
        }, () => {
          this.selectedMeasurement = {
            id: this.selected[0].measurementName.concat("_", this.selected[0].measurementTypeUnit),
            name: this.selected[0].measurementName,
            measurementTypeUnit: this.selected[0].measurementTypeUnit
          };
        });
      } else {
        this.onMeasurementSelect2(measurement);
      }
    }
  }

  private onMeasurementSelect2(measurement: EntityMeasurement) {
    this.entityFilters.measurementName = measurement.name;
    this.entityFilters.measurementTypeUnit = measurement.measurementTypeUnit === undefined ? null : measurement.measurementTypeUnit;
    this.entitiesFirst = 0;
    this.loadEntities(0, this.entitiesRows);
  }

  public onEntitiesPageChange(event: any): void {
    if (this.selectedMeasurement) {
      this.entityFilters.sortField = event.sortField;
      this.entityFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
      this.loadEntities(event.first / event.rows, event.rows);
    }
  }

  public onMeasurementsPageChange(event: any): void {
    if (this.hasBeenOpened) {
      this.measurementFilters.sortField = event.sortField;
      this.measurementFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
      this.loadEntityMeasurements(event.first / event.rows, event.rows);
    }
  }

  public isEntitySelected(entity: Entity): boolean {
    for(let selectedEntity of this.selected) {
      if (selectedEntity.uuid === entity.uuid) {
        return true;
      }
    }
    return false;
  }

  public selectEntity(entity: Entity): void {
    this.selected.push({
      uuid: entity.uuid,
      entityType: entity.entityType,
      identifier: entity.identifier,
      measurementName: this.selectedMeasurement.name,
      measurementTypeUnit: this.selectedMeasurement.measurementTypeUnit
    });
  }

  public deselectEntity(selectedEntity: SelectedEntity): void {
    this.selected.splice(this.selected.map(e => e.uuid).indexOf(selectedEntity.uuid), 1);
  }

  trimFieldsForm(filters): void {
    Object.keys(filters).forEach((key)=> {
      if (filters[key] != undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

}
